import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

interface EpUser {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  role: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  epUser: EpUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  register: (email: string, password: string, fullName: string, phone?: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [epUser, setEpUser] = useState<EpUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch ep_user profile for the current auth user
  const fetchEpUser = async (userId: string) => {
    const { data, error } = await supabase
      .from('ep_users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching ep_user:', error);
      return;
    }
    setEpUser(data as EpUser | null);
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchEpUser(session.user.id);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchEpUser(session.user.id);
        } else {
          setEpUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const register = async (email: string, password: string, fullName: string, phone?: string) => {
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          phone: phone || null,
        },
      },
    });

    if (signUpError) return { error: signUpError.message };

    if (signUpData.user) {
      // signUp doesn't always create a session — we must sign in to get auth.uid()
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        return { error: signInError.message };
      }

      // Direct insert to ep_users table (now auth.uid() is available)
      const { error: insertError } = await supabase.from('ep_users').insert({
        id: signUpData.user.id,
        email: signUpData.user.email || email,
        full_name: fullName,
        phone: phone || null,
        role: 'Danışman',
      });

      if (insertError) {
        console.error('Error inserting ep_user:', insertError);
        return { error: insertError.message };
      }
    }

    return { error: null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setEpUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, epUser, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
